import { useCallback, useEffect, useRef, useState } from 'react';

const UPDATE_CHECK_INTERVAL_MS = 60 * 1000; // 60 secondes
/** Version pour laquelle l'utilisateur a repondu « Plus tard » (le temps de l'onglet). */
const DISMISSED_KEY = 'app-update-dismissed';
/** Parametre de cache-busting pose sur l'URL du document lors d'une mise a jour. */
const BUST_PARAM = '__v';

export interface AppVersion {
  buildId: string;
  buildTime: string;
  commit: string;
}

/** Version compilee DANS le bundle en train de tourner (injectee par vite.config.ts). */
export const RUNNING_VERSION: AppVersion = {
  buildId: __APP_BUILD_ID__,
  buildTime: __APP_BUILD_TIME__,
  commit: __APP_BUILD_COMMIT__,
};

function safeSessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionSet(key: string, value: string) {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Safari en navigation privee : le bandeau reapparaitra, sans plus.
  }
}

/**
 * Lit /version.json, qui porte la version REELLEMENT deployee.
 * Ce fichier est servi en NetworkOnly par le service worker et en no-store par
 * Vercel : c'est le seul point de verite qui echappe a tous les caches.
 */
async function fetchDeployedVersion(): Promise<AppVersion | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = (await res.json()) as Partial<AppVersion>;
    if (!data?.buildId) return null;
    return {
      buildId: data.buildId,
      buildTime: data.buildTime ?? '',
      commit: data.commit ?? '',
    };
  } catch {
    // Hors ligne ou requete bloquee : on ne conclut rien.
    return null;
  }
}

/**
 * Purge totale : caches Workbox + service workers, puis rechargement sur une URL
 * cache-bustee. Sans la purge, un rechargement est resservi par le precache du
 * service worker (navigateFallback: index.html) et ramene le MEME bundle.
 *
 * Declenche UNIQUEMENT par un geste de l'utilisateur.
 */
export async function forceAppUpdate(targetBuildId?: string) {
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch (error) {
    console.error('Purge des caches impossible :', error);
  }

  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
  } catch (error) {
    console.error('Desinscription du service worker impossible :', error);
  }

  const url = new URL(window.location.href);
  url.searchParams.set(BUST_PARAM, targetBuildId ?? Date.now().toString());
  window.location.replace(url.toString());
}

/**
 * Detection de version — SANS rechargement automatique.
 *
 * Rien n'est jamais applique dans le dos de l'utilisateur : un rechargement en
 * pleine saisie (reservation, score) coute plus cher qu'une version en retard de
 * quelques minutes. Le hook se contente de signaler, le geste vient de l'utilisateur
 * (bandeau AppUpdateBanner, ou carte « Version de l'application » dans Reglages).
 * C'est aussi pourquoi `registerType` vaut 'prompt' et non 'autoUpdate' dans
 * vite.config.ts : en autoUpdate, le plugin recharge la page de lui-meme.
 *
 * La verite est la comparaison entre la version COMPILEE dans le bundle courant et
 * celle publiee dans /version.json. Aucun etat persiste, donc rien a desynchroniser.
 * Le service worker fournit un second signal (un worker installe en attente) quand
 * /version.json est injoignable.
 *
 * A appeler une seule fois dans un composant toujours monte (ex: App).
 */
export function useAppUpdate() {
  const [deployedVersion, setDeployedVersion] = useState<AppVersion | null>(null);
  const [swUpdateWaiting, setSwUpdateWaiting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [dismissedBuildId, setDismissedBuildId] = useState<string | null>(() => safeSessionGet(DISMISSED_KEY));
  const swRegistrationRef = useRef<ServiceWorkerRegistration | null>(null);

  const newVersionOnline = deployedVersion !== null && deployedVersion.buildId !== RUNNING_VERSION.buildId;
  const updateAvailable = newVersionOnline || swUpdateWaiting;
  /** Cle de la version proposee : sert a ne re-proposer qu'une seule fois par version. */
  const pendingBuildId = deployedVersion?.buildId ?? (swUpdateWaiting ? 'service-worker' : null);
  const dismissed = pendingBuildId !== null && dismissedBuildId === pendingBuildId;

  const refreshDeployedVersion = useCallback(async (): Promise<AppVersion | null> => {
    const deployed = await fetchDeployedVersion();
    if (deployed) setDeployedVersion(deployed);
    return deployed;
  }, []);

  /** Verification manuelle (bouton « Verifier les mises a jour »). */
  const checkForUpdate = useCallback(async (): Promise<AppVersion | null> => {
    setChecking(true);
    const deployed = await refreshDeployedVersion();
    setChecking(false);
    return deployed;
  }, [refreshDeployedVersion]);

  /** Applique la mise a jour — uniquement sur action de l'utilisateur. */
  const applyUpdate = useCallback(async () => {
    const deployed = deployedVersion ?? (await fetchDeployedVersion());
    await forceAppUpdate(deployed?.buildId);
  }, [deployedVersion]);

  /** « Plus tard » : masque le bandeau jusqu'a la prochaine version proposee. */
  const dismissUpdate = useCallback(() => {
    if (!pendingBuildId) return;
    safeSessionSet(DISMISSED_KEY, pendingBuildId);
    setDismissedBuildId(pendingBuildId);
  }, [pendingBuildId]);

  // Enregistrement du service worker, a la main.
  // On n'utilise PAS `virtual:pwa-register` : son client pose un
  // `addEventListener('controlling', () => window.location.reload())` et, en
  // 'autoUpdate', un rechargement des l'activation. Ici rien ne recharge la page.
  useEffect(() => {
    if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;

    let cancelled = false;

    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((registration) => {
        if (cancelled) return;
        swRegistrationRef.current = registration;

        // Un worker deja en attente = une version prete a etre installee.
        if (registration.waiting && navigator.serviceWorker.controller) {
          setSwUpdateWaiting(true);
        }

        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            // `controller` non nul => il y avait deja une version installee :
            // celle-ci est bien une mise a jour, pas la premiere installation.
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              setSwUpdateWaiting(true);
            }
          });
        });

        registration.update();
      })
      .catch((error) => {
        console.error("Erreur lors de l'enregistrement du service worker :", error);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Nettoie le parametre de cache-busting laisse par forceAppUpdate.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(BUST_PARAM)) return;
    url.searchParams.delete(BUST_PARAM);
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  }, []);

  // Verification au lancement, toutes les 60 s, et au retour au premier plan
  // (sur iOS le JS est suspendu en arriere-plan : le timer ne suffit pas).
  useEffect(() => {
    void refreshDeployedVersion();

    const interval = window.setInterval(() => {
      swRegistrationRef.current?.update();
      void refreshDeployedVersion();
    }, UPDATE_CHECK_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      swRegistrationRef.current?.update();
      void refreshDeployedVersion();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshDeployedVersion]);

  return {
    runningVersion: RUNNING_VERSION,
    deployedVersion,
    updateAvailable,
    /** true si l'utilisateur a repondu « Plus tard » pour cette version-la. */
    dismissed,
    checking,
    checkForUpdate,
    applyUpdate,
    dismissUpdate,
  };
}

export type UseAppUpdateResult = ReturnType<typeof useAppUpdate>;
